import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';
import { customBaseQuery } from '@/lib/baseQuery';
import {
  mapObjectKeysToCamelCase,
  mapObjectKeysToSnakeCase,
} from '@/utils/mapper';

export type FactfindAssessmentType = 'purchase' | 'refinance' | 'pre_approval';

export type Factfind = {
  id: number;
  assessmentType: FactfindAssessmentType;
  assessmentId: string | null;
  data: Record<string, any>;
  createdAt: string;
  updatedAt: string;
};

type FactfindPayload = {
  assessmentType: FactfindAssessmentType;
  assessmentId: string;
  data: Record<string, any>;
};

// ponytail: mapper is shallow, so nested keys inside `data`
// (e.g. previousAddresses[].startDate) stay camelCase on the backend.
const toFactfind = (raw: Record<string, any>): Factfind => {
  const ff = mapObjectKeysToCamelCase(raw) as Factfind;
  return { ...ff, data: mapObjectKeysToCamelCase(ff.data || {}) };
};

const toBody = ({ assessmentType, assessmentId, data }: FactfindPayload) => ({
  assessment_type: assessmentType,
  assessment_id: assessmentId,
  data: mapObjectKeysToSnakeCase(data),
});

export const factfindAPI = createApi({
  reducerPath: 'factfindAPI',
  baseQuery: customBaseQuery,
  tagTypes: ['Factfind'],
  endpoints: (builder) => ({
    getFactfindList: builder.query<Factfind[], void>({
      query: () => 'api/factfind/',
      transformResponse: (res: ApiResponse<Record<string, any>[]>) =>
        (res.data || []).map(toFactfind),
      providesTags: ['Factfind'],
    }),
    createFactfind: builder.mutation<Factfind, FactfindPayload>({
      query: (payload) => ({
        url: 'api/factfind/',
        method: 'POST',
        body: toBody(payload),
      }),
      transformResponse: (res: ApiResponse<Record<string, any>>) =>
        toFactfind(res.data),
      invalidatesTags: ['Factfind'],
    }),
    updateFactfind: builder.mutation<
      Factfind,
      FactfindPayload & { id: number }
    >({
      query: ({ id, ...payload }) => ({
        url: `api/factfind/${id}/`,
        method: 'PATCH',
        body: toBody(payload),
      }),
      transformResponse: (res: ApiResponse<Record<string, any>>) =>
        toFactfind(res.data),
      invalidatesTags: ['Factfind'],
    }),
  }),
});

export const {
  useGetFactfindListQuery,
  useCreateFactfindMutation,
  useUpdateFactfindMutation,
} = factfindAPI;
